import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import OrderTimeline from "@/components/feature/OrderTimeline";
import { useAuth } from "@/store/AuthContext";
import { useShop } from "@/store/ShopContext";
import { supabase } from "@/lib/supabase";
import { currencySymbol, formatPrice } from "@/pages/home/components/ProductCard";

interface OrderRow {
  id: number;
  status: string | null;
  currency: string;
  subtotal_items: number | string | null;
  shipping_total: number | string | null;
  tax_total: number | string | null;
  discount_price: number | string | null;
  tracking_number: string | null;
  recipient: Record<string, string> | null;
  created_at: string;
  order_items: { id: number; quantity: number; product_name: string }[];
}

function formatDate(iso: string) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("pt-BR", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function totalOf(o: OrderRow): number {
  return (
    Number(o.subtotal_items ?? 0) +
    Number(o.shipping_total ?? 0) +
    Number(o.tax_total ?? 0) -
    Number(o.discount_price ?? 0)
  );
}

const STATUS_PILL: Record<string, { label: string; cls: string; icon: string }> = {
  pending_payment: {
    label: "Aguardando pagamento",
    cls: "bg-secondary-100 text-secondary-900",
    icon: "ri-time-line",
  },
  paid: { label: "Pago", cls: "bg-accent-100 text-accent-900", icon: "ri-check-line" },
  processing: {
    label: "Processando",
    cls: "bg-accent-100 text-accent-900",
    icon: "ri-tools-line",
  },
  shipped: {
    label: "Enviado",
    cls: "bg-foreground-950 text-background-50",
    icon: "ri-truck-line",
  },
  delivered: {
    label: "Entregue",
    cls: "bg-accent-500 text-background-50",
    icon: "ri-checkbox-circle-line",
  },
  cancelled: {
    label: "Cancelado",
    cls: "bg-background-200 text-foreground-700",
    icon: "ri-close-circle-line",
  },
  refunded: {
    label: "Reembolsado",
    cls: "bg-background-200 text-foreground-700",
    icon: "ri-refund-2-line",
  },
};

export default function AccountDashboardPage() {
  const { user, loading: authLoading, signOut } = useAuth();
  const { favorites, showToast } = useShop();
  const navigate = useNavigate();

  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading || !user) return;
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: err } = await supabase
          .from("order_headers")
          .select("*, order_items(id, quantity, product_name)")
          .eq("customer_id", user.id)
          .order("created_at", { ascending: false });
        if (err) throw err;
        if (alive) setOrders((data ?? []) as OrderRow[]);
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Não foi possível carregar a conta";
        if (alive) setError(msg);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [user, authLoading]);

  const stats = useMemo(() => {
    const completedish = orders.filter(
      (o) => o.status !== "cancelled" && o.status !== "refunded",
    );
    const totalSpent = completedish.reduce((s, o) => s + totalOf(o), 0);
    const inProgress = orders.filter((o) =>
      ["paid", "processing", "shipped"].includes(o.status ?? ""),
    ).length;
    const delivered = orders.filter((o) => o.status === "delivered").length;
    const itemsBought = orders.reduce(
      (s, o) =>
        s +
        (o.status === "cancelled" || o.status === "refunded"
          ? 0
          : o.order_items.reduce((x, i) => x + i.quantity, 0)),
      0,
    );
    const currency = orders[0]?.currency ?? "BRL";
    return {
      ordersCount: orders.length,
      favoritesCount: favorites.length,
      totalSpent,
      inProgress,
      delivered,
      itemsBought,
      currency,
    };
  }, [orders, favorites]);

  const activeOrder = useMemo(() => {
    // Show timeline for the most-recent non-cancelled, non-delivered order
    const ongoing = orders.find(
      (o) =>
        o.status !== "delivered" &&
        o.status !== "cancelled" &&
        o.status !== "refunded",
    );
    return ongoing ?? orders[0] ?? null;
  }, [orders]);

  if (!authLoading && !user) {
    return <Navigate to="/auth?redirect=/account" replace />;
  }

  const initial = (user?.email ?? "L")[0]?.toUpperCase() ?? "L";
  const displayName =
    (user?.user_metadata?.full_name as string | undefined) ||
    user?.email?.split("@")[0] ||
    "cliente";
  const memberSince = user?.created_at
    ? new Date(user.created_at).toLocaleDateString("pt-BR", {
        month: "long",
        year: "numeric",
      })
    : "";

  const handleSignOut = async () => {
    await signOut();
    showToast("Saiu da conta");
    navigate("/", { replace: true });
  };

  const sym = currencySymbol(stats.currency);

  return (
    <div className="min-h-screen bg-background-50 text-foreground-900 flex flex-col">
      <Navbar />

      <main className="flex-1">
        {/* Header band */}
        <section className="max-w-[1120px] mx-auto w-full px-4 md:px-8 pt-6 md:pt-8 pb-2">
          <nav aria-label="Breadcrumb" className="text-[13px] text-foreground-600 mb-4">
            <ol className="flex items-center gap-1.5">
              <li>
                <Link to="/" className="hover:underline cursor-pointer">
                  Início
                </Link>
              </li>
              <li>
                <i className="ri-arrow-right-s-line text-foreground-400"></i>
              </li>
              <li className="text-foreground-900">Minha Conta</li>
            </ol>
          </nav>

          <div className="flex flex-wrap items-center justify-between gap-4 mb-6 md:mb-8">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-foreground-950 text-background-50 flex items-center justify-center font-heading text-3xl">
                {initial}
              </div>
              <div>
                <p className="text-[12px] uppercase tracking-[0.18em] text-foreground-600 mb-0.5">
                  Olá, {displayName}
                </p>
                <h1 className="font-heading text-foreground-950 text-3xl md:text-4xl">
                  Minha conta
                </h1>
                {memberSince && (
                  <p className="text-[12.5px] text-foreground-600 mt-1">
                    <i className="ri-seedling-line align-middle mr-1"></i>
                    Cliente desde {memberSince}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Link
                to="/account/orders"
                className="h-10 px-4 inline-flex items-center gap-2 rounded-full bg-foreground-950 text-background-50 text-[13px] font-medium transition-colors cursor-pointer whitespace-nowrap hover:bg-foreground-800"
              >
                <i className="ri-shopping-bag-3-line"></i>
                Todos os pedidos
              </Link>
              <Link
                to="/favorites"
                className="h-10 px-4 inline-flex items-center gap-2 rounded-full border border-background-300 hover:border-foreground-900 text-[13px] font-medium text-foreground-900 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-heart-line"></i>
                Favoritos
              </Link>
              <button
                type="button"
                onClick={handleSignOut}
                className="h-10 px-4 inline-flex items-center gap-2 rounded-full border border-background-300 hover:border-foreground-900 text-[13px] font-medium text-foreground-900 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-logout-box-r-line"></i>
                Sair
              </button>
            </div>
          </div>
        </section>

        {/* Loading / error states */}
        {loading && (
          <section className="max-w-[1120px] mx-auto w-full px-4 md:px-8 pb-16">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 animate-pulse">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="h-32 bg-background-100 rounded-2xl"></div>
              ))}
            </div>
          </section>
        )}

        {!loading && error && (
          <section className="max-w-[1120px] mx-auto w-full px-4 md:px-8 pb-16">
            <div className="bg-primary-100/70 border border-primary-200 rounded-2xl p-6 text-center">
              <p className="text-[14px] text-primary-700 mb-3">{error}</p>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="h-10 px-4 inline-flex items-center gap-2 rounded-full bg-foreground-950 text-background-50 text-[13px] font-medium cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i>
                Tentar novamente
              </button>
            </div>
          </section>
        )}

        {!loading && !error && (
          <>
            {/* Stat cards */}
            <section className="max-w-[1120px] mx-auto w-full px-4 md:px-8 pb-8">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                {/* Orders */}
                <StatCard
                  label="Pedidos"
                  value={String(stats.ordersCount)}
                  hint={
                    stats.inProgress > 0
                      ? `${stats.inProgress} em andamento`
                      : stats.ordersCount > 0
                      ? "Tudo em dia"
                      : "Nenhum ainda"
                  }
                  icon="ri-shopping-bag-3-line"
                  accent="primary"
                  to="/account/orders"
                />
                {/* Favoritos */}
                <StatCard
                  label="Favoritos"
                  value={String(stats.favoritesCount)}
                  hint={
                    stats.favoritesCount > 0
                      ? "Guardados para depois"
                      : "Toque no coração em qualquer peça"
                  }
                  icon="ri-heart-line"
                  accent="accent"
                  to="/favorites"
                />
                {/* Total gasto */}
                <StatCard
                  label="Total gasto"
                  value={`${sym}${formatPrice(stats.totalSpent)}`}
                  hint={
                    stats.itemsBought > 0
                      ? `Em ${stats.itemsBought} ${
                          stats.itemsBought === 1 ? "peça" : "peças"
                        }`
                      : "Em 0 peças"
                  }
                  icon="ri-wallet-3-line"
                  accent="secondary"
                />
                {/* Entregues */}
                <StatCard
                  label="Entregues"
                  value={String(stats.delivered)}
                  hint={
                    stats.delivered > 0
                      ? "Já nas suas mãos"
                      : "Aguardando a primeira entrega"
                  }
                  icon="ri-checkbox-circle-line"
                  accent="foreground"
                />
              </div>
            </section>

            {/* Active order timeline */}
            <section className="max-w-[1120px] mx-auto w-full px-4 md:px-8 pb-8">
              {activeOrder ? (
                <div className="bg-background-100/40 rounded-3xl p-5 md:p-7 border border-background-200/60">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <div>
                      <p className="text-[11.5px] uppercase tracking-[0.18em] text-foreground-500 mb-1">
                        {activeOrder.status === "delivered"
                          ? "Pedido mais recente"
                          : "Em andamento"}
                      </p>
                      <h2 className="font-heading text-foreground-950 text-xl md:text-2xl">
                        Pedido #{activeOrder.id}
                      </h2>
                      <p className="text-[12.5px] text-foreground-600 mt-1">
                        Feito em {formatDate(activeOrder.created_at)}
                        {activeOrder.order_items.length > 0 &&
                          ` · ${activeOrder.order_items.reduce(
                            (s, i) => s + i.quantity,
                            0,
                          )} itens`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {(() => {
                        const meta =
                          STATUS_PILL[activeOrder.status ?? ""] ??
                          STATUS_PILL.pending_payment;
                        return (
                          <span
                            className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[12px] font-medium ${meta.cls}`}
                          >
                            <i className={meta.icon}></i>
                            {meta.label}
                          </span>
                        );
                      })()}
                      <Link
                        to={`/order-confirmation/${activeOrder.id}`}
                        className="h-9 px-3.5 inline-flex items-center gap-1.5 rounded-full border border-background-300 hover:border-foreground-900 text-[12.5px] font-medium text-foreground-900 transition-colors cursor-pointer whitespace-nowrap bg-background-50"
                      >
                        <i className="ri-file-list-3-line"></i>
                        Ver recibo
                      </Link>
                    </div>
                  </div>

                  <OrderTimeline
                    status={activeOrder.status}
                    createdAt={activeOrder.created_at}
                    trackingNumber={activeOrder.tracking_number}
                  />
                </div>
              ) : (
                <EmptyState />
              )}
            </section>

            {/* Recent orders + quick links */}
            <section className="max-w-[1120px] mx-auto w-full px-4 md:px-8 pb-16">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {/* Recent orders */}
                <div className="lg:col-span-2 bg-background-50 rounded-2xl border border-background-200/70 p-5 md:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading text-foreground-950 text-lg">
                      Pedidos recentes
                    </h3>
                    <Link
                      to="/account/orders"
                      className="text-[12.5px] text-foreground-700 hover:text-foreground-950 underline underline-offset-4 cursor-pointer"
                    >
                      Ver todos
                    </Link>
                  </div>

                  {orders.length === 0 ? (
                    <div className="py-10 text-center">
                      <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-background-100 flex items-center justify-center">
                        <i className="ri-shopping-bag-3-line text-foreground-700 text-[24px]"></i>
                      </div>
                      <p className="text-[14px] text-foreground-700 mb-4">
                        Nenhum pedido ainda — seus próximos achados ficarão aqui.
                      </p>
                      <Link
                        to="/"
                        className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-foreground-950 hover:bg-foreground-800 text-background-50 text-[13px] font-medium transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-store-2-line"></i>
                        Explorar a loja
                      </Link>
                    </div>
                  ) : (
                    <ul className="divide-y divide-background-200/60">
                      {orders.slice(0, 4).map((o) => {
                        const meta =
                          STATUS_PILL[o.status ?? ""] ?? STATUS_PILL.pending_payment;
                        const itemCount = o.order_items.reduce(
                          (s, i) => s + i.quantity,
                          0,
                        );
                        return (
                          <li
                            key={o.id}
                            className="py-3.5 flex items-center justify-between gap-3"
                          >
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <p className="text-[14px] font-medium text-foreground-950">
                                  #{o.id}
                                </p>
                                <span
                                  className={`inline-flex items-center gap-1 h-5 px-2 rounded-full text-[10.5px] font-medium ${meta.cls}`}
                                >
                                  <i className={meta.icon}></i>
                                  {meta.label}
                                </span>
                              </div>
                              <p className="text-[12.5px] text-foreground-600 truncate">
                                {formatDate(o.created_at)} · {itemCount}{" "}
                                {itemCount === 1 ? "peça" : "peças"}
                                {o.order_items[0]
                                  ? ` · ${o.order_items[0].product_name}${
                                      o.order_items.length > 1
                                        ? ` +${o.order_items.length - 1}`
                                        : ""
                                    }`
                                  : ""}
                              </p>
                            </div>
                            <div className="flex items-center gap-3 shrink-0">
                              <p className="text-[14px] font-semibold text-foreground-950 whitespace-nowrap">
                                {sym}
                                {formatPrice(totalOf(o))}
                              </p>
                              <Link
                                to={`/order-confirmation/${o.id}`}
                                aria-label={`Abrir pedido ${o.id}`}
                                className="w-9 h-9 rounded-full border border-background-300 hover:border-foreground-900 text-foreground-900 flex items-center justify-center transition-colors cursor-pointer"
                              >
                                <i className="ri-arrow-right-line"></i>
                              </Link>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>

                {/* Quick actions / shortcuts */}
                <div className="space-y-4">
                  <div className="bg-accent-100 rounded-2xl p-5 md:p-6">
                    <p className="text-[11.5px] uppercase tracking-[0.18em] text-accent-900/80 mb-2">
                      Dica do dia
                    </p>
                    <h4 className="font-heading text-accent-900 text-lg leading-snug mb-2">
                      Salve o que você ama — vai ficar aqui esperando por você.
                    </h4>
                    <p className="text-[13px] text-accent-900/80 leading-6 mb-4">
                      Os favoritos ficam salvos na sua conta, mesmo depois de sair.
                    </p>
                    <Link
                      to="/favorites"
                      className="inline-flex items-center gap-2 h-9 px-4 rounded-full bg-foreground-950 text-background-50 text-[12.5px] font-medium hover:bg-foreground-800 cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-heart-line"></i>
                      Abrir favoritos
                    </Link>
                  </div>

                  <div className="bg-background-50 rounded-2xl border border-background-200/70 p-5">
                    <p className="text-[11.5px] uppercase tracking-[0.18em] text-foreground-500 mb-3">
                      Atalhos
                    </p>
                    <ul className="space-y-1">
                      <QuickLink
                        to="/account/orders"
                        icon="ri-shopping-bag-3-line"
                        label="Todos os pedidos"
                      />
                      <QuickLink
                        to="/favorites"
                        icon="ri-heart-line"
                        label="Favoritos salvos"
                      />
                      <QuickLink to="/" icon="ri-store-2-line" label="Voltar à loja" />
                      <QuickLink
                        to="/checkout"
                        icon="ri-shopping-cart-2-line"
                        label="Meu carrinho"
                      />
                    </ul>
                  </div>
                </div>
              </div>
            </section>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}

/* ------------------------------- Sub bits ------------------------------- */

interface StatCardProps {
  label: string;
  value: string;
  hint: string;
  icon: string;
  accent: "primary" | "accent" | "secondary" | "foreground";
  to?: string;
}

function StatCard({ label, value, hint, icon, accent, to }: StatCardProps) {
  const accentMap: Record<StatCardProps["accent"], string> = {
    primary: "bg-primary-100 text-primary-700",
    accent: "bg-accent-100 text-accent-900",
    secondary: "bg-secondary-100 text-secondary-900",
    foreground: "bg-foreground-950 text-background-50",
  };

  const content = (
    <>
      <div className="flex items-center justify-between mb-4">
        <div
          className={`w-10 h-10 rounded-full flex items-center justify-center ${accentMap[accent]}`}
        >
          <i className={`${icon} text-[18px]`}></i>
        </div>
        {to && (
          <i className="ri-arrow-right-up-line text-foreground-500 group-hover:text-foreground-950 transition-colors"></i>
        )}
      </div>
      <p className="text-[11.5px] uppercase tracking-[0.18em] text-foreground-600 mb-1">
        {label}
      </p>
      <p className="font-heading text-foreground-950 text-2xl md:text-3xl leading-none mb-1">
        {value}
      </p>
      <p className="text-[12px] text-foreground-600 mt-1.5">{hint}</p>
    </>
  );

  const baseClass =
    "block bg-background-50 rounded-2xl border border-background-200/70 p-4 md:p-5 transition-colors";

  if (to) {
    return (
      <Link to={to} className={`${baseClass} group hover:border-foreground-900 cursor-pointer`}>
        {content}
      </Link>
    );
  }
  return <div className={baseClass}>{content}</div>;
}

function QuickLink({ to, icon, label }: { to: string; icon: string; label: string }) {
  return (
    <li>
      <Link
        to={to}
        className="flex items-center justify-between gap-2 py-2.5 px-2 -mx-2 rounded-lg hover:bg-background-100 cursor-pointer transition-colors"
      >
        <span className="inline-flex items-center gap-2.5 text-[13.5px] text-foreground-900">
          <i className={`${icon} text-[16px] text-foreground-700`}></i>
          {label}
        </span>
        <i className="ri-arrow-right-s-line text-foreground-500"></i>
      </Link>
    </li>
  );
}

function EmptyState() {
  return (
    <div className="bg-background-100/40 rounded-3xl border border-background-200/60 px-6 py-12 text-center">
      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-50 border border-background-200/70 flex items-center justify-center">
        <i className="ri-compass-3-line text-foreground-700 text-[26px]"></i>
      </div>
      <h2 className="font-heading text-foreground-950 text-2xl mb-2">
        Sua conta está vazia por enquanto
      </h2>
      <p className="text-[14px] text-foreground-700 max-w-md mx-auto mb-6">
        Quando você fizer o seu primeiro pedido, acompanhe a jornada aqui — do ateliê à sua porta.
      </p>
      <Link
        to="/"
        className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-foreground-950 hover:bg-foreground-800 text-background-50 text-[14px] font-medium transition-colors cursor-pointer whitespace-nowrap"
      >
        <i className="ri-store-2-line"></i>
        Descobrir a loja
      </Link>
    </div>
  );
}